if (!customElements.get('mk-featured-showcase')) {
  customElements.define('mk-featured-showcase', class extends HTMLElement {
    connectedCallback() {
      this.dialog = this.querySelector('[data-mk-dialog]');
      this.content = this.querySelector('[data-mk-dialog-content]');
      this.reviewCache = new Map();
      this.abort = new AbortController();
      const signal = this.abort.signal;
      this.addEventListener('click', event => {
        const link = event.target.closest('[data-mk-showcase]');
        if (!link || event.metaKey || event.ctrlKey || event.shiftKey || event.altKey || event.button > 0) return;
        const template = document.getElementById(link.dataset.mkShowcase);
        if (!template || !this.dialog.showModal) return;
        event.preventDefault();
        this.open(template, link);
      }, { signal });
      this.dialog.addEventListener('click', event => {
        if (event.target === this.dialog || event.target.closest('[data-mk-close]')) this.dialog.close();
        if (event.target.closest('[data-mk-scroll-reviews]')) this.content.querySelector('.mk-showcase__reviews')?.scrollIntoView({ behavior: matchMedia('(prefers-reduced-motion: reduce)').matches ? 'instant' : 'smooth', block: 'start' });
      }, { signal });
      this.dialog.addEventListener('close', () => {
        this.orderObserver?.disconnect();
        this.unlock();
        this.fetchAbort?.abort();
        this.content.replaceChildren();
        this.trigger?.focus({ preventScroll: true });
      }, { signal });
    }
    disconnectedCallback() {
      this.orderObserver?.disconnect();
      this.fetchAbort?.abort();
      this.abort?.abort();
      if (this.dialog?.open) this.dialog.close();
      this.unlock();
    }
    unlock() {
      if (!this.locked) return;
      Object.assign(document.body.style, this.previousBody);
      window.scrollTo(0, this.scrollY);
      this.locked = false;
    }
    open(template, trigger) {
      this.trigger = trigger;
      this.content.replaceChildren(template.content.cloneNode(true));
      const page = this.content.querySelector('.mk-showcase__page');
      page.dataset.reviewLayout = this.dataset.reviewLayout;
      this.dialog.setAttribute('aria-labelledby', page.querySelector('h2').id);
      this.scrollY = window.scrollY;
      this.previousBody = { position: document.body.style.position, top: document.body.style.top, width: document.body.style.width, overflow: document.body.style.overflow };
      Object.assign(document.body.style, { position: 'fixed', top: `-${this.scrollY}px`, width: '100%', overflow: 'hidden' });
      this.locked = true;
      this.dialog.showModal();
      this.dialog.scrollTop = 0;
      this.watchOrderButton(page);
      this.dialog.querySelector('[data-mk-close]').focus({ preventScroll: true });
      if (page.dataset.reviewsEnabled === 'true') {
        this.rating(page);
        this.loadReviews(page);
      }
    }
    watchOrderButton(page) {
      this.orderObserver?.disconnect();
      const mainOrder = page.querySelector('.mk-showcase__intro .mk-showcase__cta');
      const anchored = page.querySelector('.mk-showcase__sticky');
      if (!mainOrder || !anchored) return;
      const setVisible = visible => {
        anchored.classList.toggle('is-visible', visible);
        anchored.inert = !visible;
        anchored.setAttribute('aria-hidden', String(!visible));
      };
      setVisible(false);
      if (!('IntersectionObserver' in window)) return;
      this.orderObserver = new IntersectionObserver(entries => {
        if (!this.dialog.open || !page.isConnected) return;
        const entry = entries[entries.length - 1];
        setVisible(!entry.isIntersecting);
      }, { root: this.dialog, threshold: 0 });
      this.orderObserver.observe(mainOrder);
    }
    rating(page, score, count) {
      const button = page.querySelector('[data-mk-scroll-reviews]');
      if (!button) return;
      score = Math.min(5, Math.max(0, Number(score ?? button.dataset.score) || 0));
      count = Math.max(0, Number(count ?? button.dataset.count) || 0);
      const stars = button.querySelector('.mk-showcase__stars');
      stars.replaceChildren();
      for (let i = 0; i < 5; i++) {
        const star = document.createElement('span'); star.className = 'mk-showcase__star';
        const svg = document.createElementNS('http://www.w3.org/2000/svg', 'svg');
        svg.setAttribute('viewBox', '0 0 24 24'); svg.setAttribute('focusable', 'false');
        const shape = document.createElementNS(svg.namespaceURI, 'path');
        shape.setAttribute('d', 'M12 2.4 15 8.5 21.8 9.5 16.9 14.3 18.1 21.1 12 17.9 5.9 21.1 7.1 14.3 2.2 9.5 9 8.5Z');
        shape.setAttribute('fill','none'); shape.setAttribute('stroke','currentColor'); shape.setAttribute('stroke-width','1.7'); shape.setAttribute('stroke-linejoin','round');
        svg.append(shape); star.append(svg);
        const fill = document.createElement('span'); fill.className = 'mk-showcase__star-fill';
        fill.style.width = `${Math.max(0, Math.min(1, score - i)) * 100}%`;
        const full = svg.cloneNode(true); full.firstChild.setAttribute('fill','currentColor'); fill.append(full); star.append(fill); stars.append(star);
      }
      const label = count ? `${this.dialog.dataset.ratingFormat.replace('[[rating]]', score.toFixed(2))} | ${this.dialog.dataset.countFormat.replace('[[count]]', count.toLocaleString())}` : this.dialog.dataset.noRating;
      button.querySelector('.mk-showcase__rating-text').textContent = label;
      button.setAttribute('aria-label', label);
    }
    async loadReviews(page) {
      this.fetchAbort?.abort(); this.fetchAbort = new AbortController();
      const list = page.querySelector('.mk-showcase__review-list');
      try {
        const url = new URL(page.dataset.productUrl, location.origin);
        url.searchParams.set('section_id', 'mk-featured-review-data');
        const preview = new URL(location.href).searchParams.get('preview_theme_id');
        if (preview) url.searchParams.set('preview_theme_id', preview);
        let data = this.reviewCache.get(url.href);
        if (!data) {
          const response = await fetch(url, { signal: this.fetchAbort.signal, credentials: 'same-origin' });
          if (!response.ok) throw new Error('Reviews unavailable');
          const parsed = new DOMParser().parseFromString(await response.text(), 'text/html');
          const raw = parsed.querySelector('template[data-mk-review-data]');
          if (!raw) throw new Error('Reviews unavailable');
          const widget = raw.content.querySelector('[data-average-rating]');
          data = { score: widget?.getAttribute('data-average-rating'), count: widget?.getAttribute('data-number-of-reviews'), reviews: Array.from(raw.content.querySelectorAll('.jdgm-rev')).map(review => ({
            body: review.querySelector('.jdgm-rev__body')?.textContent.trim(),
            title: review.querySelector('.jdgm-rev__title')?.textContent.trim(),
            author: review.querySelector('.jdgm-rev__author')?.textContent.trim(),
            score: Number(review.querySelector('.jdgm-rev__rating')?.dataset.score) || 0,
            date: review.querySelector('time')?.getAttribute('datetime'),
            etsy: !!review.querySelector('[data-badge-type="review_written_in_etsy"]')
          })).filter(review => review.body) };
          this.reviewCache.set(url.href, data);
        }
        if (!page.isConnected) return;
        this.rating(page, data.score, data.count);
        list.replaceChildren();
        if (!data.reviews.length) { const message = document.createElement('p'); message.textContent = Number(data.count) > 0 ? this.dialog.dataset.error : this.dialog.dataset.empty; list.append(message); return; }
        data.reviews.slice(0, Number(page.dataset.reviewLimit) || 3).forEach(review => {
          const figure = document.createElement('figure'); figure.className = 'mk-showcase__review';
          const quote = document.createElement('span'); quote.className = 'mk-showcase__quote'; quote.textContent = '”'; quote.setAttribute('aria-hidden','true'); figure.append(quote);
          const score = document.createElement('div'); score.className = 'mk-showcase__review-score'; score.textContent = '★'.repeat(Math.round(Math.min(5,Math.max(0,review.score)))); score.setAttribute('aria-label',`${review.score} / 5`); figure.append(score);
          const text = document.createElement('blockquote');
          if (review.title) { const title = document.createElement('strong'); title.className='mk-showcase__review-title'; title.textContent=review.title; text.append(title); }
          text.append(document.createTextNode(review.body)); figure.append(text);
          const author = document.createElement('figcaption'); author.textContent = review.author || '';
          if (review.etsy) author.append(document.createTextNode(' · Etsy'));
          if (this.dataset.reviewDates === 'true' && review.date && !isNaN(new Date(review.date))) { const time = document.createElement('time'); time.dateTime=review.date; time.textContent=` · ${new Date(review.date).toLocaleDateString()}`; author.append(time); }
          figure.append(author); list.append(figure);
        });
      } catch (error) {
        if (error.name === 'AbortError' || !page.isConnected) return;
        const message = document.createElement('p'); message.textContent=this.dialog.dataset.error; list.replaceChildren(message);
      }
    }
  });
}

